import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import { useBrowsedFolderId } from '../../../hooks/knowledge-base/useBrowsedFolderId.js';
import { useFileSystemNodes } from '../../../hooks/knowledge-base/useFileSystemNodes.js';
import { useSearchValue } from '../../../hooks/useSearchValue.js';
import type {
  FileNode,
  FileSystemNode,
  FolderNode,
} from '../../../types/fileSystem.js';
import type { CheckboxItem } from '../FileSelection/CheckboxGroup.jsx';
import { MobileFileSelectionContent } from '../FileSelection/MobileFileSelectionContent.jsx';
import { DesktopNodeSelectionContent } from './DesktopNodeSelectionContent.jsx';

type Props = {
  isDisabled: boolean;
  selectedFiles: string[];
  selectedFolders: string[];
  onSelectFiles: Dispatch<SetStateAction<string[]>>;
  onSelectFolders: Dispatch<SetStateAction<string[]>>;
  isMobile?: boolean;
};

const NodeSelection = ({
  isDisabled,
  selectedFiles,
  selectedFolders,
  onSelectFiles,
  onSelectFolders,
  isMobile = false,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const [isOpen, setIsOpen] = useState(false);

  const {
    value: searchValue,
    debouncedValue: debouncedSearchValue,
    onChange: handleChangeSearch,
    onClear: handleClearSearch,
  } = useSearchValue();

  const [currentFolderId, setCurrentFolderId] = useBrowsedFolderId();

  const {
    folders: listedFolders,
    files: availableFiles,
    breadcrumbItems,
    ownPrivateBucket,
    isLoading: areFilesLoading,
  } = useFileSystemNodes({
    folderId: currentFolderId,
    search: debouncedSearchValue,
  });

  const availableFolders = useMemo<FolderNode[]>(() => {
    const name = t('My private folder');
    const query = debouncedSearchValue.trim().toLowerCase();

    if (!ownPrivateBucket || !name.toLowerCase().includes(query)) {
      return listedFolders;
    }

    return [{ ...ownPrivateBucket, name }, ...listedFolders];
  }, [ownPrivateBucket, listedFolders, debouncedSearchValue, t]);

  const selectedFileNames = new Set(selectedFiles);
  const selectedFolderIds = new Set(selectedFolders);

  const files: Array<FileNode & CheckboxItem> = availableFiles
    .filter((f) => f.status === 'success')
    .map((f) => ({
      ...f,
      id: f.fileId,
      label: f.fileName,
      checked: selectedFileNames.has(f.fileName),
    }));

  const folders: Array<FolderNode & CheckboxItem> = availableFolders.map(
    (folder) => ({
      ...folder,
      // id included
      label: folder.name,
      checked: selectedFolderIds.has(folder.id), // Folders can have duplicate names
    }),
  );

  const handleItemChange = (
    label: string,
    checked: boolean,
    kind: FileSystemNode['kind'] = 'file',
  ) => {
    if (kind === 'folder') {
      onSelectFolders((prev) => {
        if (checked) {
          return [...prev, label];
        } else {
          return prev.filter((node) => node !== label);
        }
      });
    } else {
      onSelectFiles((prev) => {
        if (checked) {
          return [...prev, label];
        } else {
          return prev.filter((node) => node !== label);
        }
      });
    }
  };

  const handleSelectAll = () => {
    onSelectFiles(files.map((f) => f.fileName));
    onSelectFolders(folders.map((f) => f.id));
  };

  const handleDeselectAll = () => {
    onSelectFiles([]);
    onSelectFolders([]);
  };

  const sharedProps = {
    isDisabled,
    isOpen,
    onOpenChange: setIsOpen,
    files,
    folders,
    areFilesLoading,
    onItemChange: handleItemChange,
    onSelectAll: handleSelectAll,
    onDeselectAll: handleDeselectAll,
    onOpenFolder: (folder: FolderNode) => setCurrentFolderId(folder.id),
    onCrumbNavigate: setCurrentFolderId,
    breadcrumbItems,
    searchValue,
    handleChangeSearch,
    handleClearSearch,
  };

  if (isMobile) {
    return <MobileFileSelectionContent {...sharedProps} />;
  }

  return <DesktopNodeSelectionContent {...sharedProps} />;
};

export { NodeSelection };
